import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTheme } from '@/shared/hooks/use-theme';
import { PRODGY_BRAND } from '@/shared/constants/colors';
import { useChartTheme } from './chartTheme';

interface GaugeChartProps {
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  config: {
    xAxis?: string;
    yAxis?: string;
    groupBy?: string;
    value?: string;
    maxValue?: number;
    threshold?: {
      warning?: number;
      critical?: number;
    };
  };
}

export const GaugeChart = ({ data, config }: GaugeChartProps) => {
  const { theme } = useTheme();
  const chart = useChartTheme();

  // `isDark` sobrevive só para a intensidade do brilho do arco, que é efeito
  // visual e não cor de superfície. Todo o resto vem do contrato de tokens.
  const isDark = theme === 'dark';

  // Avaliar expressão do transform se existir
           // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const evaluateExpression = (expr: string, context: any): number => {
    try {
      // Suporte para expressões como "data.workItems.length"
      const keys = expr.split('.');
      let result = context;
      
      for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
          result = result[key];
        } else {
          return 0;
        }
      }
      
      return typeof result === 'number' ? result : (Array.isArray(result) ? result.length : 0);
    } catch {
      return 0;
    }
  };

  const gaugeValue = config.value 
    ? evaluateExpression(config.value, { data }) 
    : (data.value || data[0]?.value || 0);
    
  const max = config.maxValue || data.max || 100;

  const option: EChartsOption = {
    backgroundColor: chart.background,
    textStyle: { color: chart.axisLabel },
    series: [
      {
        type: 'gauge',
        startAngle: 200,
        endAngle: -20,
        min: 0,
        max: max,
        radius: '80%',
        center: ['50%', '55%'],
        splitNumber: 0,  // Remove divisões para visual clean
        progress: {
          show: true,
          width: 12,
          itemStyle: {
            color: PRODGY_BRAND.primary,
            shadowColor: isDark ? PRODGY_BRAND.primary + '60' : PRODGY_BRAND.primary + '30',
            shadowBlur: 15,
            shadowOffsetX: 0,
            shadowOffsetY: 5,
          },
        },
        axisLine: {
          lineStyle: {
            width: 12,
            color: [[1, chart.track]],
          },
          roundCap: true,
        },
        pointer: {
          show: false,  // Remove ponteiro para design clean
        },
        axisTick: {
          show: false,  // Remove ticks
        },
        splitLine: {
          show: false,  // Remove linhas de divisão
        },
        axisLabel: {
          show: false,  // Remove labels do arco
        },
        detail: {
          valueAnimation: true,
          formatter: (value: number) => {
            const formatted = value % 1 === 0 ? value : value.toFixed(1);
            return `{value|${formatted}}\n{unit|/ ${max}}`;
          },
          rich: {
            value: {
              fontSize: 48,
              fontWeight: 700,
              color: chart.text,
              lineHeight: 56,
            },
            unit: {
              fontSize: 14,
              fontWeight: 400,
              color: chart.axisLabel,
              padding: [8, 0, 0, 0],
            },
          },
          offsetCenter: [0, '10%'],
        },
        title: {
          show: true,
          offsetCenter: [0, '-30%'],
          fontSize: 13,
          fontWeight: 500,
          color: chart.axisLabel,
        },
        data: [
          {
            value: gaugeValue,
            name: data.name || '',
          },
        ],
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};
